/**
 * 휴가 길이 문구 — **서버와 같은 규칙**을 화면에서도 쓴다 (근태 B단계, 2026-08-24).
 *
 * 진실은 분이다(`hr_leaves.minutes`). 일수는 나눠서 보여 주는 파생값이고, 나누는 값은
 * **그 사람의 소정근로시간**이다 — 1일이 8시간이 아닌 사람이 있어서(단시간 근로자),
 * 일수만 들고 시간을 만들면 그 사람에게는 아예 틀린 값이 된다.
 *
 * ★ 서버 `hr-helpers.ts` 의 `formatLeaveAmount` 와 **같은 문구**를 만든다. 두 곳에서 따로
 *   만들면 같은 신청이 목록에서는 0.25일, 결재함에서는 2시간으로 보인다.
 * ★ 옛 서버는 `minutes`·`amountLabel`·`dailyMinutes` 를 주지 않는다 — 그때는 일수 문구로
 *   조용히 되돌아간다(화면이 비지 않는다).
 */

/** 근무제가 없을 때의 하루 소정근로(분). 서버 `DEFAULT_DAILY_WORK_MINUTES` 와 같은 값. */
export const DEFAULT_DAILY_WORK_MINUTES = 480;

function safeDaily(dailyMinutes?: number | null): number {
  if (typeof dailyMinutes !== 'number' || !Number.isFinite(dailyMinutes) || dailyMinutes <= 0) {
    return DEFAULT_DAILY_WORK_MINUTES;
  }
  return Math.round(dailyMinutes);
}

/**
 * `1일` · `4시간` · `1일 4시간`.
 *
 * ⛔ **이것은 «길이 표기»가 아니다** (2026-09-26). 공용 `formatWorkMinutes` 와 헷갈리지 말 것 —
 *   여기는 `dailyMinutes`(그 사람 하루 소정)를 받아 **«일»로 접는다.** 같은 480분이 8시간
 *   근무자에게는 「1일」, 6시간 근무자에게는 「1일 2시간」이다. **휴가 차감 단위**이지
 *   시간 길이가 아니라서, 공용 함수로 모을 수 있는 대상이 아니다.
 *   (정본 = `lib/daily-work.ts` 의 `formatWorkMinutes` · 면제 목록은 `lib/duration-format.test.ts`)
 */
export function formatLeaveAmount(minutes: number, dailyMinutes?: number | null): string {
  const daily = safeDaily(dailyMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return '0일';
  const whole = Math.floor(minutes / daily);
  const rest = minutes - whole * daily;
  const parts: string[] = [];
  if (whole > 0) parts.push(`${whole}일`);
  if (rest > 0) {
    const h = Math.floor(rest / 60);
    const m = rest % 60;
    if (h > 0) parts.push(`${h}시간`);
    if (m > 0) parts.push(`${m}분`);
  }
  return parts.length > 0 ? parts.join(' ') : '0일';
}

/** 일수만 있는 옛 응답을 위한 되돌림 — 정수면 `3일`, 소수면 `0.25일`. */
export function formatLeaveDays(days: number): string {
  if (!Number.isFinite(days) || days <= 0) return '0일';
  return `${Number.isInteger(days) ? days : Math.round(days * 100) / 100}일`;
}

/**
 * 신청 한 건의 길이 문구.
 *
 * 우선순위: 서버가 만든 문구 → 저장된 분 → 일수. 위에서 하나라도 있으면 그것을 쓴다.
 */
export function leaveAmountText(
  row: { days: number; minutes?: number | null; amountLabel?: string | null },
  dailyMinutes?: number | null,
): string {
  if (row.amountLabel) return row.amountLabel;
  if (typeof row.minutes === 'number' && Number.isFinite(row.minutes)) {
    // ★ 분과 일수가 함께 있으면 그 행의 소정근로를 되찾을 수 있다(minutes ÷ days) —
    //   남의 신청이 섞인 목록에서도 "1일" 을 "6시간" 이라 쓰지 않는다.
    const daily = row.days > 0 ? Math.round(row.minutes / row.days) : safeDaily(dailyMinutes);
    return formatLeaveAmount(row.minutes, daily);
  }
  return formatLeaveDays(row.days);
}

/** 요약(잔여·사용·대기)의 길이 문구 — 분이 있으면 시간까지, 없으면 일수. */
export function leaveTotalText(days: number, minutes?: number | null, dailyMinutes?: number | null): string {
  if (typeof minutes === 'number' && Number.isFinite(minutes)) return formatLeaveAmount(minutes, dailyMinutes);
  return formatLeaveDays(days);
}
